import api from 'api/api'
import { accessTokens } from 'api/accessTokenMgmt'
import { answerImageDownloadUrl } from 'lib/answerImageDownloadUrl'
import cn from 'classnames'
import OrdinalsIcon from 'components/shared/OrdinalsIcon'
import WithTooltip from 'components/shared/WithTooltip'
import React, { useRef, useState, useEffect, useMemo } from 'react'
import { isIOS, isMobile } from 'lib/device'
import useStore from 'state/knovStore'
import styles from '../answer.module.scss'
import { OrdinalInfoPopup } from './OrdinalInfoPopup'
import { confirmAsync } from 'components/shared/confirmUtils'
import { useBsvExchangeRate, useBsvFeeRate } from 'api/useBsvExchangeRate'
import { SATOSHIS } from 'lib/bsv-util'
import {
    mediaBucketsEnabled,
    bucketedMediaHeight,
    resolveMediaFit,
    payloadMediaContentWidth,
} from 'lib/mediaBuckets'
import { useTemporalWorkflowStatus } from 'hooks/useTemporalWorkflowStatus'
import {
    showOrdinalMintStartedToast,
    showOrdinalMintSuccessToast,
    showOrdinalMintErrorToast,
} from 'components/shared/OrdinalMintStatusListener'
import { BSV_TX_STATES } from 'utils/bsvTransactionStatus'
import { bsvTxExplorerUrl } from 'utils/bsvExplorerUrls'
import BsvTransactionStatusIcon from 'components/shared/BsvTransactionStatusIcon'
import { Disc3, Download, Image as ImageIcon, Pencil, Sparkles } from 'lucide-react'
import { dispatchMediaAiActionWhenThreadReady } from 'utils/mediaAiActionDispatcher'
import { areSamePreviewUrl, getLastUrlPathSegment } from '../../payloadRemoval'
import { isBrowserDisplayableImage } from '../payloadImageLightbox'

/**
 * Check if the answer's image was pasted in, in which case we can use the local link and not go to the server.
 * @param {import('types/answers').Answer} answer - The answer object to check
 * @returns {boolean} - Whether the answer has a blob URL
 */
const hasBlob = answer => {
    const url = answer?.local_answer_image_url || answer?.answer_image_url
    return typeof url === 'string' && (url.startsWith('blob:') || url.startsWith('data:'))
}

const normalizeApiUrl = rawUrl => {
    if (typeof rawUrl !== 'string' || !rawUrl) return rawUrl
    if (
        rawUrl.startsWith('//') ||
        rawUrl.startsWith('http://') ||
        rawUrl.startsWith('https://') ||
        rawUrl.startsWith('blob:') ||
        rawUrl.startsWith('data:')
    ) {
        return rawUrl
    }

    if (rawUrl.startsWith('/api/') && window.knovApiUrl) {
        const base = String(window.knovApiUrl).replace(/\/+$/, '')
        return `${base}${rawUrl}`
    }

    return rawUrl
}
const isGifMedia = ({ url = null, contentType = null, label = null } = {}) => {
    const normalizedContentType = String(contentType || '').toLowerCase()
    const normalizedLabel = String(label || '').toLowerCase()
    const normalizedUrl = String(url || '')
        .split(/[?#]/)[0]
        .toLowerCase()
    return (
        normalizedContentType === 'image/gif' ||
        normalizedLabel === 'gif' ||
        normalizedUrl.endsWith('.gif')
    )
}
const MINT_TX_OVERHEAD_BYTES = 600
const MINT_FEE_SAFETY_MARGIN = 1.2
const ANSWER_IMAGE_LOAD_CACHE_LIMIT = 300
const loadedAnswerImagesByKey = new Map()

const rememberLoadedAnswerImage = (cacheKey, imageUrl, frameHeight = null) => {
    if (!cacheKey || !imageUrl) return

    if (loadedAnswerImagesByKey.has(cacheKey)) {
        loadedAnswerImagesByKey.delete(cacheKey)
    }
    loadedAnswerImagesByKey.set(cacheKey, { url: imageUrl, height: frameHeight })

    while (loadedAnswerImagesByKey.size > ANSWER_IMAGE_LOAD_CACHE_LIMIT) {
        const oldestKey = loadedAnswerImagesByKey.keys().next().value
        if (!oldestKey) break
        loadedAnswerImagesByKey.delete(oldestKey)
    }
}

const forgetLoadedAnswerImage = cacheKey => {
    if (!cacheKey) return
    loadedAnswerImagesByKey.delete(cacheKey)
}

const rememberedLoadedAnswerImage = (cacheKey, imageUrl) => {
    if (!cacheKey || !imageUrl) return null

    const remembered = loadedAnswerImagesByKey.get(cacheKey)
    return remembered?.url === imageUrl ? remembered : null
}

/**
 * Component that displays an answer's image with associated controls and overlays
 * @type {React.ForwardRefExoticComponent<{
 *   answer: import('types/answers').Answer,
 *   draftAnswer?: import('types/answers').Answer,
 *   toggle?: (url?: string) => void,
 *   isThin?: boolean,
 *   showDownload?: boolean,
 *   isEmbed?: boolean,
 *   isTitle?: boolean,
 *   postLens?: boolean,
 *   estimatedWidth?: number,
 *   isActive?: boolean,
 *   path?: string,
 *   imageSize?: string,
 *   uiId?: string,
 *   hasSeenGenerating?: boolean,
 *   contextStyles?: any,
 *   placeholderSize?: { width: number, height: number },
 *   quest?: any,
 *   openRight?: (filter: any) => Promise<any>,
 *   sourcePanel?: any,
 *   downloading?: boolean,
 *   onMediaLayoutSettled?: () => void
 * } & React.RefAttributes<HTMLImageElement>>}
 */
const AnswerImage = React.forwardRef((props, ref) => {
    const [hasImageError, setHasImageError] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [loadedImageUrl, setLoadedImageUrl] = useState(null)
    const [loadedImageFrameHeight, setLoadedImageFrameHeight] = useState(null)
    const [isOrdinalInfoVisible, setIsOrdinalInfoVisible] = useState(false)
    const [isImageActionMenuOpen, setIsImageActionMenuOpen] = useState(false)
    // Workflow tracking state (no longer persisted on answer, set on mint initiation)
    const [mintingWorkflowId, setMintingWorkflowId] = useState(null)
    const [mintingRunId, setMintingRunId] = useState(null)
    const [mintingAccessToken, setMintingAccessToken] = useState(null)
    const ordinalButtonRef = useRef(null)
    const imageActionMenuRef = useRef(null)

    const DEBUG = useStore(state => state.DEBUG)
    const openOrdinalsModal = useStore(state => state.actions.modalActions.openOrdinalsModal)
    const answer = props.answer
    const currentUserId = window.gon?.currentUser?.id
    const isAnswerOwner = answer?.user_id === currentUserId
    const isAuthenticated = Boolean(currentUserId)
    const draftAnswer = props.draftAnswer
    const isThin = props?.isThin || false
    const imagesV2 = Array.isArray(answer?.images_v2) ? answer.images_v2 : []
    const latestImageV2 = imagesV2[0]
    const imageV2Urls = latestImageV2?.urls || {}
    const imageUrls = Array.isArray(answer?.image_urls) ? answer.image_urls : []
    const latestImage = imageUrls[imageUrls.length - 1]
    const previewUrls = latestImage?.preview_urls || {}
    const downloadUrl = answerImageDownloadUrl(window.knovApiUrl, String(answer?.id), [
        imageV2Urls.original,
        previewUrls.original,
        latestImage?.image_url,
        answer?.answer_image_url,
    ])
    let url

    const bsvExchangeRate = useBsvExchangeRate()
    const bsvFeeRateSatsPerByte = useBsvFeeRate()

    // Temporal workflow status tracking for ordinal minting with EventBus support
    // Must have all three: workflowId, runId, AND accessToken before we start polling
    const shouldTrackMintWorkflow = Boolean(
        isAuthenticated && isAnswerOwner && mintingWorkflowId && mintingRunId && mintingAccessToken,
    )

    const isAuthenticationError = errorMessage => {
        if (typeof errorMessage !== 'string') {
            return false
        }
        return errorMessage.includes('sign in or sign up before continuing')
    }

    const {
        isPolling: isMintingInProgress,
        startPolling: startMintingPolling,
        error: mintingError,
        latestMessage,
        isCompleted,
    } = useTemporalWorkflowStatus(
        mintingWorkflowId,
        mintingRunId,
        'progress',
        'AppTemporal::Workflows::MintOrdinalWorkflow',
        {
            enabled: shouldTrackMintWorkflow,
            pollInterval: 2000, // Slower polling since EventBus provides real-time updates
            eventBusTopic: 'mint_ordinal_status',
            logPrefix: 'Minting',
            accessToken: mintingAccessToken || undefined,
            onComplete: async result => {
                console.log('🎉 Mint ordinal completed successfully!')
                setIsLoading(false) // Stop spinner

                // Clear workflow IDs FIRST to prevent the useEffect from interfering
                setMintingWorkflowId(null)
                setMintingRunId(null)
                setMintingAccessToken(null)

                // Same toast OrdinalMintStatusListener shows; single slot dedupes the
                // double delivery when both EventBus and polling report completion.
                showOrdinalMintSuccessToast(answer?.id)
            },
            onError: error => {
                console.error('Minting failed:', error)
                setIsLoading(false)
                setMintingWorkflowId(null)
                setMintingRunId(null)
                setMintingAccessToken(null)

                if (!isAnswerOwner || isAuthenticationError(error)) {
                    return
                }

                showOrdinalMintErrorToast(answer?.id, error)
            },
        },
    )

    // Start polling when workflow IDs are set
    useEffect(() => {
        if (shouldTrackMintWorkflow && !isMintingInProgress) {
            startMintingPolling()
        }
    }, [shouldTrackMintWorkflow, startMintingPolling, isMintingInProgress])

    // Clear workflow IDs if ordinal exists (mint was completed elsewhere)
    useEffect(() => {
        if (answer?.has_ordinal && (mintingWorkflowId || mintingRunId) && !isMintingInProgress) {
            // Clear only if we're not actively polling
            // This handles the case where mint was completed on another device/tab
            setMintingWorkflowId(null)
            setMintingRunId(null)
            setMintingAccessToken(null)
        }
    }, [answer?.has_ordinal, mintingWorkflowId, mintingRunId, isMintingInProgress])

    // Clear workflow IDs when mint fails
    useEffect(() => {
        if (mintingError && (mintingWorkflowId || mintingRunId)) {
            setMintingWorkflowId(null)
            setMintingRunId(null)
            setMintingAccessToken(null)
            // Note: setIsLoading(false) is already handled in onError callback
        }
    }, [mintingError, mintingWorkflowId, mintingRunId])

    useEffect(() => {
        if (!isImageActionMenuOpen) return undefined

        const closeOnOutsidePointer = ev => {
            if (imageActionMenuRef.current?.contains(ev.target)) return
            setIsImageActionMenuOpen(false)
        }

        document.addEventListener('pointerdown', closeOnOutsidePointer, true)
        return () => {
            document.removeEventListener('pointerdown', closeOnOutsidePointer, true)
        }
    }, [isImageActionMenuOpen])

    // Lightbox URL: always use original resolution for full-quality viewing
    let lightboxUrl
    if (hasBlob(draftAnswer)) {
        url = draftAnswer.local_answer_image_url || draftAnswer.answer_image_url
        lightboxUrl = url // Blob URLs are already original
    } else if (hasBlob(answer)) {
        url = answer.local_answer_image_url || answer.answer_image_url
        lightboxUrl = url // Blob URLs are already original
    } else {
        const baseUrl = `${window.knovApiUrl}/api/v1/answers/${answer?.id}/image`
        const imageSize = props.imageSize
        const isPrivateImageV2 = latestImageV2?.access === 'private'
        const directPreviewUrl = isPrivateImageV2
            ? null
            : (imageSize ? imageV2Urls?.[imageSize] : null) ||
              imageV2Urls?.medium ||
              imageV2Urls?.small ||
              latestImageV2?.preview_url ||
              (imageSize ? previewUrls?.[imageSize] : null) ||
              previewUrls?.medium ||
              previewUrls?.small ||
              latestImage?.preview_url ||
              answer?.answer_image_url ||
              latestImage?.image_url ||
              null
        const originalIsDisplayable = isBrowserDisplayableImage({
            contentType: latestImageV2?.image_content_type || latestImage?.image_content_type,
            filename: latestImageV2?.image_filename || latestImage?.image_filename,
        })
        const directOriginalUrl = isPrivateImageV2
            ? null
            : !originalIsDisplayable
            ? imageV2Urls?.large || previewUrls?.large || null
            : imageV2Urls?.original ||
              previewUrls?.original ||
              latestImage?.image_url ||
              answer?.answer_image_url ||
              null

        // Prefer an image-specific cache key to avoid busting on unrelated updates
        // 1) Use LATEST image attachment_id when available (answer.image returns last)
        // 2) Fallback: answer updated_at (stable but less precise)
        const attachmentId = latestImageV2?.attachment_id || latestImage?.attachment_id
        const cacheKey = attachmentId || (answer?.updated_at ? String(answer.updated_at) : '')
        const visibilityToken = answer?.image_visibility_token
        const shouldUseVisibilityToken = Boolean(visibilityToken)

        if (directPreviewUrl && !hasImageError) {
            url = normalizeApiUrl(directPreviewUrl)
            lightboxUrl = normalizeApiUrl(directOriginalUrl || directPreviewUrl)
        } else {
            // Build display URL (may include size param for variants)
            let imageQueryString = `?v=${encodeURIComponent(cacheKey || '')}`
            if (imageSize) {
                imageQueryString = imageQueryString
                    ? `${imageQueryString}&size=${imageSize}`
                    : `?size=${imageSize}`
            }
            if (attachmentId) {
                imageQueryString = imageQueryString
                    ? `${imageQueryString}&attachment_id=${encodeURIComponent(attachmentId)}`
                    : `?attachment_id=${encodeURIComponent(attachmentId)}`
            }
            if (hasImageError) {
                // Force Rails to bypass token-driven CDN mode on retry and issue a fresh non-CDN redirect.
                imageQueryString = imageQueryString
                    ? `${imageQueryString}&break_cache=true&force_nocdn=true`
                    : '?break_cache=true&force_nocdn=true'
            }
            if (shouldUseVisibilityToken) {
                imageQueryString = imageQueryString
                    ? `${imageQueryString}&vtoken=${encodeURIComponent(visibilityToken)}`
                    : `?vtoken=${encodeURIComponent(visibilityToken)}`
            }
            url = `${baseUrl}${imageQueryString}`

            // Lightbox URL: no size param = original resolution, unless the
            // original can't render in a browser (e.g. HEIC)
            let lightboxQuery = `?v=${encodeURIComponent(cacheKey || '')}`
            if (!originalIsDisplayable) {
                lightboxQuery = `${lightboxQuery}&size=large`
            }
            if (attachmentId) {
                lightboxQuery = `${lightboxQuery}&attachment_id=${encodeURIComponent(attachmentId)}`
            }
            if (hasImageError) {
                // Keep lightbox consistent with inline image fallback behavior.
                lightboxQuery = `${lightboxQuery}&force_nocdn=true`
            }
            if (shouldUseVisibilityToken) {
                lightboxQuery = `${lightboxQuery}&vtoken=${encodeURIComponent(visibilityToken)}`
            }
            lightboxUrl = `${baseUrl}${lightboxQuery}`
        }
    }

    const imageCacheKey = useMemo(() => {
        const attachmentId = latestImageV2?.attachment_id || latestImage?.attachment_id
        const blobId = latestImageV2?.blob_id
        if (attachmentId && blobId) return `attachment:${attachmentId}:blob:${blobId}`
        if (blobId) return `blob:${blobId}`
        if (attachmentId) return `attachment:${attachmentId}`
        if (answer?.id && url) return `answer:${answer.id}:${url}`
        return url || null
    }, [
        answer?.id,
        latestImage?.attachment_id,
        latestImageV2?.attachment_id,
        latestImageV2?.blob_id,
        url,
    ])
    const rememberedImage = hasImageError ? null : rememberedLoadedAnswerImage(imageCacheKey, url)
    const hasLoadedImage = Boolean(url && (loadedImageUrl === url || rememberedImage))
    const loadedFrameHeight =
        loadedImageFrameHeight?.url === url
            ? loadedImageFrameHeight.height
            : rememberedImage?.height
    const useStableImageFrame = !isThin

    let name
    const path = props.path
    if (path) {
        const arr = path.split('/')
        name = arr[arr.length - 1]
    } else {
        name = 'knov'
    }

    const matchingBsvAttachment = answer?.bsv_attachments?.find(
        att =>
            att.attachment_url == answer.answer_image_url ||
            answer.image_urls?.some(img => img.attachment_id === att.attachment_id),
    )

    const hasMintableImage = Boolean(
        answer?.answer_image_url ||
            (Array.isArray(answer?.image_urls) && answer.image_urls.length > 0),
    )
    const latestImageForActions = Array.isArray(answer?.image_urls)
        ? answer.image_urls[answer.image_urls.length - 1]
        : null
    const latestImageV2ForActions = Array.isArray(answer?.images_v2) ? answer.images_v2[0] : null
    const imageActionInputUrl =
        latestImageV2ForActions?.urls?.original ||
        latestImageForActions?.preview_urls?.original ||
        answer?.image_urls?.[answer.image_urls.length - 1]?.image_url ||
        answer?.answer_image_url ||
        lightboxUrl ||
        url ||
        null
    const hasImageActionMenu = Boolean(isAuthenticated && !isThin && imageActionInputUrl)
    const imageActionLabel = answer?.user?.name || 'Answer image'
    const imageOrdinal = answer?.image_bsv_ordinal || null
    const hasMintedImage = Boolean(imageOrdinal?.id || answer?.has_ordinal)
    const isImageMinting = Boolean(
        isLoading || isMintingInProgress || answer?.mint_workflow_id || answer?.mint_run_id,
    )
    const imageOrdinalRecord = Object(imageOrdinal || {})
    const imageOrdinalOrigin = imageOrdinal?.origin || ''
    const ordinalTransactionId =
        imageOrdinalRecord?.mint_tx?.tx_id ||
        imageOrdinalRecord?.mint_tx_tx_id ||
        imageOrdinalRecord?.tx_id ||
        imageOrdinalOrigin.split(/[_:]/)[0] ||
        null
    const matchingBsvAttachmentTx = Object(matchingBsvAttachment?.bsv_tx || {})
    const blockchainTransactionId = ordinalTransactionId || matchingBsvAttachmentTx?.tx_id || null
    const blockchainExplorerUrl = bsvTxExplorerUrl(blockchainTransactionId)
    const imageBlockchainActionIsOrdinal = hasMintedImage || !matchingBsvAttachment
    const imageBlockchainActionLabel = imageBlockchainActionIsOrdinal ? 'Ordinal' : 'Blockchain'
    const imageActionAttachmentId =
        latestImageV2ForActions?.attachment_id || latestImageForActions?.attachment_id || null
    const isGifImageAction = isGifMedia({
        url: imageActionInputUrl,
        contentType:
            latestImageV2ForActions?.image_content_type ||
            latestImageForActions?.image_content_type,
        label: latestImageV2ForActions?.image_filename || latestImageForActions?.image_filename,
    })
    const imageByteSize = Number(answer?.image_byte_size || 0)
    const sourceImageAiRun = useMemo(() => {
        if (!imageActionInputUrl) return null
        const runs = Array.isArray(answer?.ai_runs) ? answer.ai_runs : []
        const imageSegment = getLastUrlPathSegment(imageActionInputUrl)
        return (
            runs.find(run =>
                (Array.isArray(run?.output_urls) ? run.output_urls : []).some(output => {
                    const outputRecord = Object(output)
                    const outputUrls = Object(outputRecord.urls)
                    const outputUrl =
                        outputRecord.url ||
                        outputUrls.original ||
                        outputUrls.large ||
                        outputUrls.medium ||
                        outputUrls.small ||
                        outputRecord.preview_url ||
                        outputRecord.previewUrl
                    const outputSegment = getLastUrlPathSegment(outputUrl)
                    return (
                        outputUrl &&
                        (areSamePreviewUrl(outputUrl, imageActionInputUrl) ||
                            (imageSegment && outputSegment === imageSegment))
                    )
                }),
            ) || null
        )
    }, [answer?.ai_runs, imageActionInputUrl])
    const sourceImageAiActionPayload = useMemo(() => {
        const actionPayload = sourceImageAiRun?.action_payload
        if (!actionPayload || typeof actionPayload !== 'object') return null

        const sourceRunId = sourceImageAiRun?.id || null
        const sourceRootRunId =
            sourceImageAiRun?.root_remix_ai_run_id ||
            sourceImageAiRun?.remix_ai_run_id ||
            sourceRunId
        const existingRemixSource = actionPayload.remix_source || actionPayload.remixSource
        const remixSource =
            existingRemixSource && typeof existingRemixSource === 'object'
                ? existingRemixSource
                : {}

        return {
            ...actionPayload,
            remix_ai_run_id: sourceRunId,
            root_remix_ai_run_id: sourceRootRunId,
            remix_kind: 'remix',
            remix_source: {
                ...remixSource,
                ai_run_id: sourceRunId,
                root_remix_ai_run_id: sourceRootRunId,
                answer_id: answer?.id || null,
                attachment_id: imageActionAttachmentId || null,
            },
            tag:
                actionPayload.tag ||
                actionPayload.action_tag ||
                actionPayload.actionTag ||
                sourceImageAiRun?.action_tag ||
                null,
        }
    }, [answer?.id, imageActionAttachmentId, sourceImageAiRun])

    const insertImageAiAction = (tag, previewTitle = null) => {
        if (!imageActionInputUrl) return
        dispatchMediaAiActionWhenThreadReady({
            detail: {
                questId: props.quest?.id,
                childQuestId: answer?.child_quests?.[0]?.id || props.quest?.id || null,
                tag,
                previewTitle,
                image: {
                    url: imageActionInputUrl,
                    label: imageActionLabel,
                    answerId: answer?.id,
                    attachmentId: imageActionAttachmentId,
                    blobId: latestImageV2ForActions?.blob_id || null,
                    runId: sourceImageAiRun?.id || null,
                    rootRemixAiRunId:
                        sourceImageAiRun?.root_remix_ai_run_id ||
                        sourceImageAiRun?.remix_ai_run_id ||
                        sourceImageAiRun?.id ||
                        null,
                },
            },
            openRight: props.openRight,
            sourcePanel: props.sourcePanel,
        })
        setIsImageActionMenuOpen(false)
    }

    const remixImageAiAction = () => {
        if (!sourceImageAiActionPayload) {
            useStore.getState().showGlobalNotice('No remix metadata was found for this image.', {
                variant: 'error',
                durationMs: 5000,
            })
            setIsImageActionMenuOpen(false)
            return
        }

        dispatchMediaAiActionWhenThreadReady({
            detail: {
                questId: props.quest?.id,
                childQuestId: answer?.child_quests?.[0]?.id || props.quest?.id || null,
                aiAction: sourceImageAiActionPayload,
                remix: true,
            },
            openRight: props.openRight,
            sourcePanel: props.sourcePanel,
        })
        setIsImageActionMenuOpen(false)
    }

    const renderBsvAttachmentStatus = (matchingBsvAttachment, { disableLink = false } = {}) => {
        const bsvTx = matchingBsvAttachment?.bsv_tx
        const txId = bsvTx?.tx_id
        return (
            <BsvTransactionStatusIcon
                bsvTx={bsvTx}
                fallbackTxId={txId}
                disableLink={disableLink}
                className={styles.imageActionMenuStatusIcon}
                wrapperProps={{
                    href: bsvTxExplorerUrl(txId),
                    target: txId ? '_blank' : undefined,
                    rel: txId ? 'noopener noreferrer' : undefined,
                    onClick: ev => ev.stopPropagation(),
                }}
            />
        )
    }

    const handleMintOrdinal = async ev => {
        ev.stopPropagation()
        if (!hasMintableImage) return

        let effectiveImageByteSize = imageByteSize
        if (effectiveImageByteSize <= 0 && answer?.id) {
            try {
                const mediaInfo = await api.getMintMediaInfo(answer.id, 'image')
                effectiveImageByteSize = Number(mediaInfo?.byte_size || 0)
            } catch (error) {
                console.error('Failed to resolve image byte size for mint estimate:', error)
            }
        }

        if (effectiveImageByteSize <= 0) {
            await confirmAsync('Unable to determine image size for mint estimate.', {
                confirmLabel: 'OK',
                hideCancel: true,
                iconType: 'error',
            })
            return
        }

        if (!bsvExchangeRate || bsvExchangeRate <= 0) {
            await confirmAsync('Unable to fetch BSV exchange rate. Please try again in a moment.', {
                confirmLabel: 'OK',
                hideCancel: true,
                iconType: 'error',
            })
            return
        }

        // The server signs the fee quote shown here, so the mint start charges this
        // exact amount even if another server process has since repriced.
        const mintFeeQuote = await api.getOrdinalMintFeeQuote().catch(() => null)
        const mintPriceSats = Number(mintFeeQuote?.service_fee_satoshis)
        if (
            !Number.isSafeInteger(mintPriceSats) ||
            mintPriceSats <= 0 ||
            !mintFeeQuote?.service_fee_quote
        ) {
            await confirmAsync(
                'Unable to quote the Treechat mint fee. Please try again in a moment.',
                {
                    confirmLabel: 'OK',
                    hideCancel: true,
                    iconType: 'error',
                },
            )
            return
        }
        const mintPriceUsd = (mintPriceSats / SATOSHIS) * bsvExchangeRate
        const estimatedOnchainWriteSats = Math.ceil(
            (effectiveImageByteSize + MINT_TX_OVERHEAD_BYTES) *
                bsvFeeRateSatsPerByte *
                MINT_FEE_SAFETY_MARGIN,
        )
        const estimatedOnchainWriteUsd = (estimatedOnchainWriteSats / SATOSHIS) * bsvExchangeRate
        const estimatedTotalUsd = mintPriceUsd + estimatedOnchainWriteUsd

        // Track mint button click
        window.logEv('BSV_MINT_START', {
            answer_id: answer?.id,
            quest_id: answer?.quest_id,
            media_type: 'image',
            mint_price_sats: mintPriceSats,
            mint_price_usd: mintPriceUsd,
            image_bytes: effectiveImageByteSize || undefined,
            fee_rate_sats_per_byte: bsvFeeRateSatsPerByte,
            estimated_onchain_write_sats: estimatedOnchainWriteSats,
        })

        const confirmMintRes = await new Promise(resolve => {
            const { openConfirmTray } = useStore.getState().actions.trayActions
            const onCancel = () => resolve(false)
            const onConfirm = () => resolve(true)

            openConfirmTray(
                React.createElement(
                    'div',
                    { style: { color: 'var(--color-text-primary)' } },
                    React.createElement(
                        'div',
                        {
                            style: {
                                fontWeight: 600,
                                fontSize: '12px',
                                letterSpacing: '0.06em',
                                textTransform: 'uppercase',
                                opacity: 0.7,
                                marginBottom: '6px',
                            },
                        },
                        'Mint Ordinal',
                    ),
                    React.createElement(
                        'div',
                        { style: { opacity: 0.85, marginTop: '6px' } },
                        `Includes $${mintPriceUsd.toFixed(
                            2,
                        )} platform fee and $${estimatedOnchainWriteUsd.toFixed(2)} onchain write`,
                    ),
                ),
                `MINT $${estimatedTotalUsd.toFixed(2)}`,
                onConfirm,
                {
                    onCancel,
                    hideCancel: true,
                    confirmVariant: 'bsv',
                    primaryActionFirst: true,
                },
            )
        })

        if (!confirmMintRes) return

        setIsLoading(true)

        try {
            const mintResult = await api.mintOrdinalTemporal(answer?.id, {
                treechat_fee_amount_sats: mintPriceSats,
                service_fee_quote: mintFeeQuote.service_fee_quote,
            })
            const { workflow_id: workflowId, run_id: runId, access_token: accessToken } = mintResult

            if (workflowId && runId && accessToken) {
                // Set workflow IDs to enable status polling - completion handled by useEffect
                setMintingWorkflowId(workflowId)
                setMintingRunId(runId)
                setMintingAccessToken(accessToken)
                showOrdinalMintStartedToast(answer?.id)
            } else if (workflowId && runId) {
                // Legacy workflow or registry miss - can't poll status without token
                // Clear loading state and let EventBus handle updates
                console.warn(
                    'Mint workflow started but no access_token returned - status polling disabled',
                )
                setIsLoading(false)
            } else {
                throw new Error('Failed to start mint ordinal workflow')
            }
        } catch (error) {
            alert(`Failed to mint ordinal: ${error}`)
            console.error('Failed to mint ordinal:', error)
            setIsLoading(false)
        }
    }

    const handleDownload = ev => {
        ev.preventDefault()
        ev.stopPropagation()

        if (window.KNOVIGATOR_IS_MOBILE) {
            // Mobile download - use direct link
            window.open(downloadUrl)
        } else {
            // Desktop download - fetch and trigger download
            fetch(downloadUrl, { headers: accessTokens() })
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`Download failed: ${response.status}`)
                    }
                    return response.blob()
                })
                .then(blob => {
                    const url = window.URL.createObjectURL(blob)

                    const a = document.createElement('a')
                    a.style.display = 'none'
                    a.href = url
                    a.download = name || 'image'
                    document.body.appendChild(a)
                    a.click()

                    window.URL.revokeObjectURL(url)
                    document.body.removeChild(a)
                })
                .catch(error => {
                    console.error('Failed to download image:', error)
                    // Could show user notification here if needed
                })
        }
    }

    // Map of variant max-dimension in pixels. Must match backend IMAGE_SIZES in `Answer` model.
    const SIZE_TO_PX = {
        small: 120,
        medium: 600,
        large: 1050,
    }

    // Compute width/height attributes to preserve aspect ratio and reduce layout shift
    const { width: renderedWidth, height: renderedHeight } = useMemo(() => {
        const originalWidth =
            latestImageV2?.image_width || latestImage?.image_width || answer?.image_width
        const originalHeight =
            latestImageV2?.image_height || latestImage?.image_height || answer?.image_height
        const sizeKey = props.imageSize

        if (!originalWidth || !originalHeight) return { width: undefined, height: undefined }

        const px = SIZE_TO_PX[sizeKey]
        if (!px) return { width: originalWidth, height: originalHeight }

        const scale = Math.min(1, Math.min(px / originalWidth, px / originalHeight))
        return {
            width: Math.round(originalWidth * scale),
            height: Math.round(originalHeight * scale),
        }
    }, [
        answer?.image_height,
        answer?.image_width,
        latestImage?.image_height,
        latestImage?.image_width,
        latestImageV2?.image_height,
        latestImageV2?.image_width,
        props.imageSize,
    ])

    // Lock size only if generation has started and an image error occurs, using placeholder dimensions
    const applyFixedSize = props.hasSeenGenerating && props.placeholderSize

    // Prototype (flag-gated): quantize media to a fixed height per aspect class so the row
    // height is a constant the virtualizer never has to measure or re-layout. The image is
    // contained inside that frame so inspectable post content is never cropped.
    const useMediaBuckets =
        mediaBucketsEnabled() &&
        useStableImageFrame &&
        !applyFixedSize &&
        renderedWidth &&
        renderedHeight
    // The single answer image renders full-width in the payload box; the width contract
    // (lib/mediaBuckets) clamps the frame so contain-fit never reserves letterbox bands —
    // on mobile viewports and inside post-lens reply cards (indented column).
    const bucketHeight = useMediaBuckets
        ? bucketedMediaHeight(
              renderedWidth,
              renderedHeight,
              payloadMediaContentWidth({
                  postLens: !!props.postLens,
                  cardWidth: props.estimatedWidth,
              }),
          )
        : null
    const bucketFit = useMediaBuckets
        ? resolveMediaFit(renderedWidth, renderedHeight, bucketHeight)
        : null

    // Container style: lock width/height on error
    const containerStyle = {
        contain: 'paint',
        ...(useStableImageFrame && {
            '--answer-image-frame-min-height': `${bucketHeight || loadedFrameHeight || 160}px`,
        }),
        ...(useMediaBuckets && { height: `${bucketHeight}px`, width: '100%' }),
        ...(applyFixedSize && {
            aspectRatio: `${props.placeholderSize.width} / ${props.placeholderSize.height}`,
        }),
        // Do not force aspect ratio on the container in normal cases to avoid visual off-centering
    }
    // Image style: lock width/height on error
    /** @type {React.CSSProperties} */
    const imageStyle = {
        objectFit: useMediaBuckets ? bucketFit : /** @type {const} */ ('contain'),
        ...(useMediaBuckets && { width: '100%', height: '100%' }),
        ...(applyFixedSize && {
            aspectRatio: `${props.placeholderSize.width} / ${props.placeholderSize.height}`,
        }),
        // When intrinsic dims are known, let the image reserve space with its own aspect ratio
        ...(!useMediaBuckets && !applyFixedSize && renderedWidth && renderedHeight
            ? { aspectRatio: `${renderedWidth} / ${renderedHeight}` }
            : {}),
    }

    return (
        <>
            {url && (
                <div
                    style={containerStyle}
                    data-testid={hasMintedImage ? 'answer-ordinal-image' : 'answer-image'}
                    className={cn(
                        props.contextStyles,
                        useStableImageFrame && styles.answerImageFrame,
                        useStableImageFrame && hasLoadedImage && styles.answerImageFrameLoaded,
                    )}
                >
                    {useStableImageFrame && !hasLoadedImage && (
                        <div className={styles.answerImagePlaceholder} />
                    )}
                    <img
                        ref={ref}
                        style={imageStyle}
                        width={renderedWidth}
                        height={renderedHeight}
                        onClick={ev => {
                            if (typeof props.toggle !== 'function') return

                            ev.preventDefault()
                            ev.stopPropagation()
                            props.toggle(lightboxUrl)
                        }}
                        src={url}
                        // WebKit can stall lazy images mounted inside a hidden incoming panel.
                        loading={isIOS ? 'eager' : 'lazy'}
                        decoding="async"
                        onLoad={event => {
                            if (useStableImageFrame) {
                                const frameHeight = Math.ceil(
                                    event.currentTarget.parentElement?.getBoundingClientRect?.()
                                        ?.height || 0,
                                )
                                rememberLoadedAnswerImage(
                                    imageCacheKey,
                                    url,
                                    frameHeight > 0 ? frameHeight : null,
                                )
                                setLoadedImageFrameHeight(
                                    frameHeight > 0 ? { url, height: frameHeight } : null,
                                )
                                setLoadedImageUrl(url)
                            }
                            props.onMediaLayoutSettled?.()
                        }}
                        onError={e => {
                            forgetLoadedAnswerImage(imageCacheKey)
                            setHasImageError(true)
                        }}
                    />
                    {!isThin &&
                        (matchingBsvAttachment ||
                            answer?.image_bsv_ordinal?.id ||
                            props.showDownload ||
                            hasImageActionMenu ||
                            (isAnswerOwner && answer?.answer_image_url)) && (
                            <div className={styles.imageStatusContainer}>
                                {hasImageActionMenu && (
                                    <div
                                        className={styles.imageActionMenu}
                                        ref={imageActionMenuRef}
                                    >
                                        <WithTooltip tip="Image actions">
                                            <button
                                                type="button"
                                                className={cn(
                                                    styles.imageActionMenuButton,
                                                    (hasMintedImage || isImageMinting) &&
                                                        styles.ordinalMediaActionButton,
                                                )}
                                                onClick={ev => {
                                                    ev.preventDefault()
                                                    ev.stopPropagation()
                                                    setIsImageActionMenuOpen(open => !open)
                                                }}
                                                aria-label="Image actions"
                                                aria-haspopup="menu"
                                                aria-expanded={isImageActionMenuOpen}
                                            >
                                                {hasMintedImage || isImageMinting ? (
                                                    <OrdinalsIcon size={16} />
                                                ) : (
                                                    <ImageIcon size={16} strokeWidth={2.25} />
                                                )}
                                            </button>
                                        </WithTooltip>
                                        {isImageActionMenuOpen && (
                                            <div
                                                className={styles.imageActionMenuList}
                                                role="menu"
                                                onClick={ev => ev.stopPropagation()}
                                            >
                                                {blockchainExplorerUrl && (
                                                    <a
                                                        className={styles.imageActionMenuItem}
                                                        href={blockchainExplorerUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        role="menuitem"
                                                        onClick={ev => ev.stopPropagation()}
                                                    >
                                                        {imageBlockchainActionIsOrdinal ? (
                                                            <OrdinalsIcon size={14} />
                                                        ) : (
                                                            renderBsvAttachmentStatus(
                                                                matchingBsvAttachment,
                                                                { disableLink: true },
                                                            )
                                                        )}
                                                        <span>{imageBlockchainActionLabel}</span>
                                                    </a>
                                                )}
                                                {props.showDownload && (
                                                    <button
                                                        type="button"
                                                        className={styles.imageActionMenuItem}
                                                        role="menuitem"
                                                        onClick={ev => {
                                                            setIsImageActionMenuOpen(false)
                                                            handleDownload(ev)
                                                        }}
                                                    >
                                                        <Download size={14} strokeWidth={2.2} />
                                                        <span>Download</span>
                                                    </button>
                                                )}
                                                {!isGifImageAction && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className={styles.imageActionMenuItem}
                                                            role="menuitem"
                                                            onClick={() =>
                                                                insertImageAiAction(
                                                                    'nb',
                                                                    'Edit Image',
                                                                )
                                                            }
                                                        >
                                                            <Pencil size={14} strokeWidth={2.2} />
                                                            <span>Edit</span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={styles.imageActionMenuItem}
                                                            role="menuitem"
                                                            onClick={() =>
                                                                insertImageAiAction(
                                                                    'kling3',
                                                                    'Animate Image',
                                                                )
                                                            }
                                                        >
                                                            <Sparkles size={14} strokeWidth={2.2} />
                                                            <span>Animate</span>
                                                        </button>
                                                    </>
                                                )}
                                                {sourceImageAiActionPayload && (
                                                    <button
                                                        type="button"
                                                        className={styles.imageActionMenuItem}
                                                        role="menuitem"
                                                        onClick={remixImageAiAction}
                                                    >
                                                        <Disc3 size={14} strokeWidth={2.2} />
                                                        <span>Remix</span>
                                                    </button>
                                                )}
                                                {isAnswerOwner &&
                                                    hasMintableImage &&
                                                    !hasMintedImage && (
                                                        <button
                                                            type="button"
                                                            className={styles.imageActionMenuItem}
                                                            role="menuitem"
                                                            disabled={isImageMinting}
                                                            onClick={ev => {
                                                                setIsImageActionMenuOpen(false)
                                                                handleMintOrdinal(ev)
                                                            }}
                                                        >
                                                            {isLoading || isMintingInProgress ? (
                                                                <i className="fa fa-spinner fa-spin" />
                                                            ) : (
                                                                <OrdinalsIcon size={14} />
                                                            )}
                                                            <span>
                                                                {isImageMinting
                                                                    ? 'Minting...'
                                                                    : 'Mint'}
                                                            </span>
                                                        </button>
                                                    )}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {answer?.image_bsv_ordinal?.id && !hasImageActionMenu && (
                                    <div
                                        className={styles.ordinalButtonOverlay}
                                        onClick={ev => {
                                            ev.stopPropagation()
                                            setIsOrdinalInfoVisible(!isOrdinalInfoVisible)
                                        }}
                                        ref={ordinalButtonRef}
                                    >
                                        <OrdinalsIcon size={20} />
                                        {isOrdinalInfoVisible && (
                                            <OrdinalInfoPopup
                                                ordinal={answer.image_bsv_ordinal}
                                                onClose={() => setIsOrdinalInfoVisible(false)}
                                                openOrdinalsModal={openOrdinalsModal}
                                                buttonRef={ordinalButtonRef}
                                            />
                                        )}
                                    </div>
                                )}
                                {matchingBsvAttachment && (
                                    <div className={styles.bsvStatusOverlay}>
                                        {renderBsvAttachmentStatus(matchingBsvAttachment)}
                                        {false && DEBUG && (
                                            <pre style={{ width: '200px' }}>
                                                {JSON.stringify(matchingBsvAttachment, null, 2)}
                                            </pre>
                                        )}
                                    </div>
                                )}
                                {props.showDownload && (
                                    <div className={styles.downloadButtonOverlay}>
                                        <WithTooltip tip="Download image">
                                            {window.KNOVIGATOR_IS_MOBILE ? (
                                                <a
                                                    href={downloadUrl}
                                                    download={name}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={ev => {
                                                        ev.stopPropagation()
                                                    }}
                                                >
                                                    <i className="fa fa-download" />
                                                </a>
                                            ) : (
                                                <i
                                                    className="fa fa-download"
                                                    onClick={handleDownload}
                                                />
                                            )}
                                        </WithTooltip>
                                    </div>
                                )}
                            </div>
                        )}
                </div>
            )}
        </>
    )
})

export default AnswerImage
